"use client"

import {
  DashboardAreaChart as BaseAreaChart,
  DashboardDivergingAreaChart as BaseDivergingAreaChart,
  DashboardGroupedBarChart as BaseGroupedBarChart,
} from "@ibada/overview-widgets"

import { formatCompactCurrency, formatMoneySafe } from "@/features/finance/overview/lib/overview-format"

/**
 * The tenant's binding of the shared money charts.
 *
 * The charts moved to `@ibada/overview-widgets` and now take their formatters as props. That matters here:
 * `formatMoneySafe` carries ADR-0024 branch-autonomy semantics — it decides whether a figure is money or a
 * withheld ratio — and a shared chart must not carry one app's disclosure rules. Injecting them keeps that
 * judgement in the tenant, where it belongs, while the drawing is shared.
 */
const tenantMoneyFormatters = {
  formatValue: (value: number, currency: string) => formatMoneySafe(value, currency),
  formatCompact: (value: number, currency: string) => formatCompactCurrency(value, currency),
} as const

type GroupedBarProps = Omit<React.ComponentProps<typeof BaseGroupedBarChart>, "formatValue" | "formatCompact">
type DivergingAreaProps = Omit<React.ComponentProps<typeof BaseDivergingAreaChart>, "formatValue" | "formatCompact">
type AreaProps = Omit<React.ComponentProps<typeof BaseAreaChart>, "formatValue" | "formatCompact">

export function DashboardGroupedBarChart(props: GroupedBarProps) {
  return <BaseGroupedBarChart {...props} {...tenantMoneyFormatters} />
}

export function DashboardDivergingAreaChart(props: DivergingAreaProps) {
  return <BaseDivergingAreaChart {...props} {...tenantMoneyFormatters} />
}

export function DashboardAreaChart(props: AreaProps) {
  return <BaseAreaChart {...props} {...tenantMoneyFormatters} />
}
